/* globals Krux */
/* eslint no-use-before-define: 0 */

import Context from "shared/util/Context";
import ClientStats from "shared/util/ClientStats";

import {
	BIDDERS,
	REFRESH_AD_COOLDOWN_SECONDS,
} from "./constants.js";

import {
	loadGPT,
	loadPBJS,
	loadAmazon,
	loadAdomik,
	setupPBJS,
	setupGPT,
	getAdUnits,
	formatAdUnitsForPBJS,
	isDebugMode,
} from "./functions.js";

import LoadVisibleAds from "./LoadVisibleAds.js";

// attach a lot of stuff to the window
// -----------------------------------------------------------------------------

window.googletag = typeof (window) !== "undefined" && window.googletag || {};
window.googletag.cmd = window.googletag.cmd || [];

window.pbjs = typeof (window) !== "undefined" && window.pbjs || {};
window.pbjs.que = window.pbjs.que || [];


// internal state
// -----------------------------------------------------------------------------

let _keywords = [];
let _isGPTLoaded = false;
let _hasRefreshCooldown = false;

let _isRunningAuction = false;
let _isShowing = false;

let _adQueue = {};
let _adsRequested = {};
const _adsDisplayed = {};
let _slots = {};

let _callBidTimeout = null;
let _bidTimeout = null;

// this is only for BIDDING PRODUCTS
// so everything done by prebid.js is under that one bidding product
const _bidStatus = {
	[BIDDERS.prebid]: false,
	[BIDDERS.amazon]: false,
};


// Add targeting to gpt tags
// -----------------------------------------------------------------------------

function _addKeywords(slot, format) {
	Object.keys(_keywords).forEach((key) => {
		const value = String(_keywords[key]);
		slot.setTargeting(key, value);
	});

	if (format.indexOf("_addl") !== -1) {
		slot.setTargeting("pos", "instream");
	}

	if (typeof (Krux) !== "undefined") {
		slot.setTargeting("ksg", Krux.segments);
		slot.setTargeting("kuid", Krux.user);
	}
}

function _setTargeting({ ads, cb }) {
	if (!ads) {
		window.pbjs.setTargetingForGPTAsync();
	} else {
		const adCodes = ads.map((ad) => ad.code);
		window.pbjs.setTargetingForGPTAsync(adCodes);
	}

	if (typeof window.apstag !== "undefined") {
		window.apstag.setDisplayBids();
		cb();
	} else {
		cb();
	}
}


// dump those ads onto the page
// -----------------------------------------------------------------------------

function _showAds(ads = []) {
	if (!_isGPTLoaded) {
		loadGPT();
		_isGPTLoaded = true;
	}

	const newSlots = {};
	let hasNewSlots = false;

	window.googletag.cmd.push(() => {
		ads.forEach((unit) => {
			if (!_slots[unit.code]) {
				const slot = window.googletag
					.defineSlot(unit.slot, unit.sizes, unit.code)
					.addService(window.googletag.pubads())
					.setTargeting("daisy_format", unit.format);
				_addKeywords(slot, unit.format);
				newSlots[unit.code] = slot;
				hasNewSlots = true;
			}
		});

		// update slots now so they're available in _setTargeting
		if (hasNewSlots) {
			_slots = {
				..._slots,
				...newSlots,
			};

			Object.keys(newSlots).forEach((key) => {
				_slots[key] = newSlots[key];
			});
		}

		_setTargeting({
			ads,

			cb() {
				const refreshSlots = [];
				const platform = Context.get("platform");
				const daisyPage = Context.get("daisyPage");

				ads.forEach((unit) => {
					const slot = _slots[unit.code];

					// don't recreate a slot if we're doing that shit
					if (newSlots[unit.code]) {
						window.googletag.display(unit.code);
					} else {
						refreshSlots.push(_slots[unit.code]);
					}

					// update stats for displayed ads
					const login = !Context.get("userid") ? "logged out" : "logged in";

					// pretend _addls are vanilla
					const formatString = unit.format.replace("_addl", "");

					const s = [];

					s.push(`ads - onpage - ad shown - ${login}`);
					s.push(`ads - onpage - ad shown - ${login} - format:${formatString}`);
					s.push(`ads - onpage - ad shown - ${login} - platform:${platform}`);

					if (platform === "DESKTOP") {
						s.push(`ads - onpage - ad shown - ${login} - page:${daisyPage}`);
					}

					ClientStats.update(s);

					if (isDebugMode()) {
						console.log(
							"OKC", "just displayed ", unit.code, " with targeting ",
							slot.getTargetingKeys().reduce((memo, key) => {
								return {
									...memo,
									[key]: slot.getTargeting(key)[0],
								};
							}, {}),
						);
					}

					_adsDisplayed[unit.code] = unit;
					delete _adsRequested[unit.code];
				});

				if (refreshSlots.length) {
					window.googletag.pubads().refresh(refreshSlots);
				}

				_isShowing = false;
			},
		});

		// if anything's in the queue, re-run
		if (Object.values(_adQueue).length) {
			_addAdsAndBidSoon();
		}
	});
}

function _bidWithPrebid(ads) {
	// Restructure for new pbjs.
	const adUnits = formatAdUnitsForPBJS(ads);

	window.pbjs.que.push(() => {
		window.pbjs.addAdUnits(adUnits);
		window.pbjs.requestBids({
			adUnits,
			timeout: Context.get("prebidTimeout"),

			bidsBackHandler() {
				_markReady(BIDDERS.prebid);
			},
		});
	});
}

function _bidAmazon(ads) {
	window.apstag.fetchBids(

		// config
		{
			slots: ads.map(({ code, sizes }) => {
				return {
					slotID: code,
					sizes,
				};
			}),
			timeout: Context.get("prebidTimeout"),
		},

		// callback
		() => {
			_markReady(BIDDERS.amazon);
		},
	);
}

function _callBids() {
	if (_isRunningAuction) {
		return;
	}

	let requestedBids = false;

	Object.keys(_bidStatus).forEach((bidder) => {
		_bidStatus[bidder] = false;
	});
	_isRunningAuction = true;
	_adsRequested = { ..._adQueue };
	const ads = Object.values(_adQueue);
	_adQueue = {};

	/*
		Reminder; ads is an array of these objects:

		{
			bids: [ { bidder, params } ],
			top,
			bottom,
			code: id of element,
			format: leader/box/etc.,
			sizes: [[width,height]...],
			slot: slotname
		}
	*/

	requestedBids = true;
	_bidWithPrebid(ads);

	_bidAmazon(ads);

	if (!requestedBids) {
		_markReady(BIDDERS.timeout);
	} else {
		clearTimeout(_bidTimeout);
		_bidTimeout = setTimeout(() => {
			_markReady(BIDDERS.timeout);
		}, Context.get("prebidTimeout"));
	}
}

function _addAdsAndBidSoon(ads) {
	if (ads) {
		ads.forEach((ad) => {
			const id = ad.code;

			if (!_adQueue[id]) {
				_adQueue[id] = ad;
			}
		});
	}

	if (!_isRunningAuction) {
		clearTimeout(_callBidTimeout);
		_callBidTimeout = setTimeout(_callBids, 10);
	}
}

function _markReady(bidder) {
	if (!BIDDERS[bidder] || _isShowing) {
		return;
	}

	if (bidder !== BIDDERS.timeout) {
		_bidStatus[bidder] = true;
	}

	const allBidsIn = Object.keys(_bidStatus).reduce((memo, b) => {
		if (!_bidStatus[b]) {
			return false;
		}

		return memo;
	}, true);

	if (bidder === BIDDERS.timeout || allBidsIn) {
		_isRunningAuction = false;
		clearTimeout(_bidTimeout);
		const ads = Object.values(_adsRequested);
		_isShowing = true;

		const timedOut = bidder === BIDDERS.timeout;

		if (isDebugMode()) {
			console.log("OKC: timed out:", timedOut, "bids in:", allBidsIn, "showing ", ads);
		}

		_showAds(ads);
	}
}

// ad visibility culling
// -----------------------------------------------------------------------------



// interfaces
// -----------------------------------------------------------------------------

function loadAds({ units, keywords }) {
	if (!units || !keywords) {
		return;
	}

	// init the visibility manager
	LoadVisibleAds.setCallback(_addAdsAndBidSoon);

	const ads = getAdUnits(units);

	_keywords = keywords;

	setupPBJS();
	setupGPT();
	loadPBJS();
	loadAmazon();
	loadAdomik();

	LoadVisibleAds.showAdsIfVisible(ads);
}

/**
 * Create an ad placement, given an object with props for an element ID and
 * a format. The element whose ID you give should already be in the DOM.
 * @param id          {String}
 * @param format      {String}
 */

function addAd(unit) {
	const ads = getAdUnits([ unit ]);
	LoadVisibleAds.showAdsIfVisible(ads);
}

function removeAd(id) {
	if (_slots[id]) {
		window.googletag.destroySlots(_slots[id]);
		_slots[id] = null;
	}

	if (_adsDisplayed[id]) {
		console.log("OKC: removing ad: ", _adsDisplayed[id]);
		delete _adsDisplayed[id];
	}
}

function refreshAds({ force } = {}) {
	if (!force) {
		if (_hasRefreshCooldown) {
			return;
		}

		_hasRefreshCooldown = true;
		setTimeout(() => {
			_hasRefreshCooldown = false;
		}, REFRESH_AD_COOLDOWN_SECONDS * 1000);
	}

	const newAds = Object.values(_adsDisplayed).filter((unit) => {
		return document.getElementById(`${unit.code}`);
	});

	Object.keys(_adsDisplayed).forEach((key) => {
		delete _adsDisplayed[key];
	});
	Object.keys(_adsRequested).forEach((key) => {
		delete _adsRequested[key];
	});

	LoadVisibleAds.showAdsIfVisible(newAds);
}

function refreshAdsById(id) {
	const newAds = Object.values(_adsDisplayed).filter((unit) => {
		return document.getElementById(`${unit.code}`) && unit.code === id;
	});
	delete _adsDisplayed[id];
	delete _adsRequested[id];
	LoadVisibleAds.showAdsIfVisible(newAds);
}

const onPageAds = {
	refreshAds,
	addAd,
	removeAd,
	loadAds,
	refreshAdsById,

	refreshAdVisibility: LoadVisibleAds.refreshAdVisibility,

	getAll() {
		return {
			slots: _slots,
			displayed: _adsDisplayed,
			requested: _adsRequested,
			buffer: _adsRequested,
		};
	},
};

window.OnPageAds = onPageAds;
export default onPageAds;



// WEBPACK FOOTER //
// ./src/shared/modules/onpageads/index.js